import React, { FC, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { ResourceMapping } from '@ariksa/inventory-core/api';
import { Stack, Box, HStack, Divider, Center } from '@chakra-ui/react';
import { each, isEqual, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  DetailsIdentifier,
  getIcon,
  StackedCell,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { ActionButton, Select } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { ClockIcon, ExceptionIcon } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { EnvironmentName } from 'containers/App/utils';
import { AddException } from 'containers/Compliance/Components/AddException';
import {
  complianceSummary,
  renderPassedChecksSummary,
} from 'containers/Compliance/Components/ComplianceDashboard/ComplianceSummary';
import {
  renderPassedChecks,
  renderTableHeaderWithTooltip,
  renderViolations,
} from 'containers/Compliance/Components/ComplianceTables/utils';
import { History } from 'containers/Compliance/Components/History';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

import { customTheme } from '../../../../../../theme';

interface Props {
  row: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
}

export const ByResourceTypeSubComponent: FC<Props> = props => {
  const { row, isOpen, onClose } = props;
  const { byResourceTypeSummary, selectedBlueprint, environment } = useSelector(
    selectCompliance,
  );
  const { toCloudAccountId } = useCloudAccountId();
  const {
    onOpenHistory,
    isOpenSeverityModal,
    isOpenSuppress,
    onCloseSuppress,
    selectedResource,
    isOpenHistory,
    setSelectedResource,
  } = useCompliance();

  const dispatch = useDispatch();
  const [summary, setSummary] = useState<{
    data: Record<string, any>[];
    total: number;
  }>({ data: [], total: 0 });
  const [pageInfo, setPageInfo] = useState(INIT_PAGE_INFO);
  const [type, setType] = useState<ResourceMapping>({} as ResourceMapping);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortByField, setSortByField] = useState<Sorted>({
    sortField: '',
    sortOrder: '',
  });
  const { getResourceType } = useResourceType();
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const { getTabIndex } = useMetadataDrawerTabs();
  const [alertTabIndex, setAlertTabIndex] = useState(0);
  const [clouds, setClouds] = useState<string[]>([]);
  const [accountOptions, setAccountOptions] = useState<Record<string, any>[]>(
    [],
  );
  const [account, setAccount] = useState<Record<string, any>>({});
  const { accountMapping } = useCloudAccountId();

  useEffect(() => {
    setType(getResourceType(row?.type ?? '') || ({} as ResourceMapping));
  }, [getResourceType, row]);

  useEffect(() => {
    setAlertTabIndex(
      getTabIndex(type.cloud_native_class!, MetadataTabs.Alerts),
    );
  }, [getTabIndex, type]);

  //set account options and clouds
  useEffect(() => {
    let items = new Set();
    let options: Record<string, any>[] = [
      { label: 'Account', value: undefined },
    ];
    each(row?.account_ids, o => {
      const a = accountMapping?.[o];
      options.push({
        label: a?.name + ' (' + a?.cloud_account_id + ')',
        value: o,
        icon: getIcon(a?.cloud_type!),
      });
      items.add(a?.cloud_type);
    });
    setAccountOptions(options);
    setAccount(options?.[0]);
    setClouds(toArray(items) as string[]);
  }, [accountMapping, row]);

  useDeepCompareEffect(() => {
    dispatch(
      actions.getSummaryByResourceType({
        q: {
          blueprintId: selectedBlueprint?.id,
          page: pageInfo.page_number,
          size: pageInfo.page_size,
          resourceType: row?.type,
          accountId: account?.value,
          searchTerm,
          environmentId: environment?.id,
        },
        onSuccess: res => {
          setSummary({ data: res?.items || [], total: res?.total || 0 });
        },
      }),
    );
  }, [
    row,
    dispatch,
    pageInfo,
    selectedBlueprint,
    searchTerm,
    environment,
    account,
  ]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Entity</Box>,
      accessor: 'name',
      sortKey: 'name',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row.type}
          iconTooltip={<ResourceTypeIconTooltip resourceType={row?.type} />}
          iconSize="9"
        >
          <StackedCell upper={row?.name} lower={row?.cloud_resource_id} />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Type',
      accessor: 'cloud_resource_id',
      align: 'left',
      render: ({ row }) => {
        const type = getResourceType(row?.type);
        return (
          <StackedCell
            upper={type?.cloud_native_name}
            lower={type?.cloud_agnostic_name}
          />
        );
      },
    },
    {
      header: 'Deployed In',
      accessor: 'account_id',
      align: 'left',
      render: ({ value }) => toCloudAccountId(value),
    },
    {
      header: renderTableHeaderWithTooltip(
        'Exceptions',
        'All exceptions applied to this entity',
        'center',
      ),
      accessor: 'check_stats.resource_exceptions',
    },
    {
      header: 'Violations',
      accessor: 'check_stats',
      align: 'left',
      render: renderViolations,
    },
    {
      header: 'Passed Policy Checks',
      accessor: 'check_stats',
      align: 'left',
      render: renderPassedChecks,
      styles: { width: '250px', cell: { maxWidth: '250px' } },
    },
    {
      header: 'Actions',
      accessor: 'resource_id',
      render: ({ row }) => (
        <Center>
          <ActionButton
            icon={<ClockIcon />}
            label="Show historical data"
            onClick={e => {
              e.stopPropagation();
              setSelectedResource(row);
              onOpenHistory();
            }}
          />
          <ActionButton
            icon={<ExceptionIcon />}
            label="Apply exceptions for this resource"
            onClick={e => {
              e.stopPropagation();
              updateActiveResource({
                resourceType: row?.type,
                uuid: row.resource_id,
                accountId: row.account_id,
                resourceId: row.cloud_resource_id,
                cloudProvider: row?.cloud_provider?.toLowerCase() as CloudProviders,
              });
              onOpenMetadata(alertTabIndex);
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  const RenderBox = ({ children }) => (
    <Box
      border="1px solid"
      borderColor={customTheme.colors.gray['100']}
      borderRadius={6}
      w="full"
      h="full"
    >
      <Center>{children}</Center>
    </Box>
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <HStack justify="space-between">
            <StackedHeader
              upper={
                type?.cloud_native_name + ' / ' + type?.cloud_agnostic_name
              }
              lower={row?.check_stats.total_entities + ' policies'}
              type={type.cloud_native_class!}
            />
          </HStack>
        }
        styles={{
          content: { maxW: '1400px' },
        }}
        closeButton
        body={
          <Stack w="full" h="full" pt={3} spacing={5}>
            <HStack spacing={6} align="start">
              <Stack spacing={3} w="md">
                <DetailsIdentifier
                  label="Cloud"
                  value={
                    <HStack>
                      {map(clouds, o => (
                        <Center boxSize={5}>
                          <Center>{getIcon(o)}</Center>
                        </Center>
                      ))}
                    </HStack>
                  }
                />

                <DetailsIdentifier
                  label="Environment Selected"
                  value={<EnvironmentName environmentId={environment?.id} />}
                  align="center"
                />

                <Box pt={0.5}>
                  <DetailsIdentifier
                    label="Accounts With Entity Type"
                    value={row?.account_ids?.length ?? 0}
                  />
                </Box>
              </Stack>
              <Box>
                <RenderBox>
                  <Box px={4} pb={1}>
                    {complianceSummary(
                      'Resource Exceptions',
                      formatNumber(row?.check_stats?.resource_exceptions, 1),
                      false,
                    )}
                  </Box>
                </RenderBox>
              </Box>
              <Box flex={1}>
                <RenderBox>
                  {renderPassedChecksSummary(
                    row?.check_stats?.passed,
                    row?.check_stats?.total_performed,
                    Number(
                      (row?.check_stats?.passed /
                        row?.check_stats?.total_performed) *
                        100,
                    ).toFixed(0),
                  )}
                </RenderBox>
              </Box>
            </HStack>
            <Divider color="gray.200" />
            <HStack>
              <Box w={96}>
                <SearchBox
                  onChange={value => setSearchTerm(value)}
                  value={searchTerm}
                  placeholder="Search"
                />
              </Box>
              <Box pl={2}>
                <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
              </Box>
              <Box>
                <Select
                  options={accountOptions}
                  value={account}
                  onChange={setAccount}
                  isAFilter
                  menuPortalTarget={document.body}
                  showIconInValueContainer
                />
              </Box>
            </HStack>
            <Table
              data={summary.data}
              columns={columns}
              cursor="pointer"
              isLoading={byResourceTypeSummary.isLoading}
              onRowClick={row => {
                updateActiveResource({
                  resourceType: row?.type,
                  uuid: row.resource_id,
                  accountId: row.account_id,
                  resourceId: row.cloud_resource_id,
                  tabIndex: alertTabIndex,
                  cloudProvider: row?.cloud_provider?.toLowerCase() as CloudProviders,
                });
                onOpenMetadata();
              }}
              onSort={sortInfo => {
                if (!isEqual(sortByField, sortInfo)) {
                  setSortByField(sortInfo);
                }
              }}
              sortBy={sortByField}
              pagination={{
                totalCount: summary.total,
                pageInfo,
                onChange: setPageInfo,
              }}
            />
          </Stack>
        }
      />
      {isOpenSeverityModal && <SeverityComponent />}
      {isOpenSuppress && (
        <AddException
          resource={selectedResource}
          isOpen={isOpenSuppress}
          onClose={onCloseSuppress}
        />
      )}
      {isOpenHistory && <History resourceType={row?.type} />}
    </>
  );
};
